import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Building2, FileText, TrendingUp } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { DocumentViewer } from '../components/documents/DocumentViewer';
import {
  api,
  getPropertyCoverImageUrl,
  PROPERTY_IMAGE_PLACEHOLDER,
} from '../services/api';
import { formatDate } from '../utils/date';

export const InvestorMyPropertyPage: React.FC = () => {
  const { propertyId } = useParams<{ propertyId: string }>();

  const { data, isLoading, error } = useQuery({
    queryKey: ['my-investor-property', propertyId],
    queryFn: () => api.getMyInvestorProperty(propertyId!),
    enabled: !!propertyId,
    staleTime: 30_000,
  });

  if (!propertyId) {
    return (
      <div className="text-center py-12 text-gray-500">
        Falta el identificador de la propiedad.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (error || !data?.property) {
    const msg =
      error instanceof Error
        ? error.message
        : 'No se pudo cargar la propiedad o no tienes acceso.';
    return (
      <div className="max-w-lg mx-auto space-y-4 py-8">
        <Card className="p-6">
          <p className="text-gray-700">{msg}</p>
          <Link
            to="/mi-cuenta"
            className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver a Mi cuenta
          </Link>
        </Card>
      </div>
    );
  }

  const { property, documents, investments } = data as {
    property: {
      id: string;
      name: string;
      address: string;
      city: string;
      state?: string | null;
      coverImage?: string | null;
      status: string;
      m2Total?: number | null;
      pricePerM2?: number | null;
      totalValue?: number | null;
      currentValue?: number | null;
    };
    documents: Array<{
      id: string;
      title: string;
      fileUrl: string | null;
      fileType?: string | null;
      uploadedAt?: string | null;
    }>;
    investments: Array<{ ownershipPct: number; status: string }>;
  };

  const docsWithFile = documents.filter((d) => !!d.fileUrl);
  const totalPct = investments.reduce(
    (s, inv) => s + (Number(inv.ownershipPct) || 0),
    0,
  );

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/mi-cuenta"
          className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Mi cuenta
        </Link>

        <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
          <div className="flex flex-col sm:flex-row gap-4 p-6">
            <div className="w-full sm:w-40 h-28 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0 border border-gray-100">
              <img
                src={
                  property.coverImage
                    ? getPropertyCoverImageUrl(property.coverImage)
                    : PROPERTY_IMAGE_PLACEHOLDER
                }
                alt=""
                className="w-full h-full object-cover"
              />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-start gap-2">
                <Building2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <h1 className="text-xl font-bold text-gray-900">{property.name}</h1>
                  <p className="text-sm text-gray-600 mt-1">
                    {[property.address, property.city, property.state]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                  <p className="text-xs text-gray-500 mt-2 capitalize">
                    Estado del activo: {String(property.status).replace(/-/g, ' ')}
                  </p>
                  {totalPct > 0 && (
                    <p className="text-sm text-gray-700 mt-2">
                      Tu participación (suma de inversiones en este activo):{' '}
                      <span className="font-semibold">
                        {totalPct.toLocaleString('es-MX', { maximumFractionDigits: 4 })}%
                      </span>
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Valores del activo */}
      {(property.totalValue || property.currentValue || property.m2Total) && (() => {
        const fmt = (v: number) =>
          `$${v.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        const plusvalia =
          property.currentValue && property.totalValue
            ? property.currentValue - property.totalValue
            : null;
        const plusvaliaPct =
          plusvalia !== null && property.totalValue
            ? (plusvalia / property.totalValue) * 100
            : null;
        return (
          <div>
            <div className="mb-3">
              <h2 className="text-lg font-semibold text-gray-900">Valores del activo</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {property.totalValue != null && (
                <div className="rounded-xl border border-gray-200 bg-white p-4">
                  <p className="text-xs font-medium text-gray-500">Valor de adquisición</p>
                  <p className="mt-1 text-base font-bold text-gray-900">{fmt(property.totalValue)}</p>
                </div>
              )}
              {property.currentValue != null && (
                <div className="rounded-xl border border-gray-200 bg-white p-4">
                  <p className="text-xs font-medium text-gray-500">Valor actual</p>
                  <p className="mt-1 text-base font-bold text-gray-900">{fmt(property.currentValue)}</p>
                </div>
              )}
              {plusvalia !== null && (
                <div className={`rounded-xl border p-4 ${plusvalia >= 0 ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
                  <div className="flex items-center gap-1">
                    <TrendingUp className={`w-3.5 h-3.5 ${plusvalia >= 0 ? 'text-green-600' : 'text-red-600'}`} />
                    <p className="text-xs font-medium text-gray-500">Plusvalía</p>
                  </div>
                  <p className={`mt-1 text-base font-bold ${plusvalia >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                    {plusvalia >= 0 ? '+' : ''}{fmt(plusvalia)}
                  </p>
                  {plusvaliaPct !== null && (
                    <p className={`text-xs mt-0.5 ${plusvalia >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {plusvalia >= 0 ? '+' : ''}{plusvaliaPct.toFixed(2)}%
                    </p>
                  )}
                </div>
              )}
              {property.m2Total != null && (
                <div className="rounded-xl border border-gray-200 bg-white p-4">
                  <p className="text-xs font-medium text-gray-500">Superficie</p>
                  <p className="mt-1 text-base font-bold text-gray-900">
                    {Number(property.m2Total).toLocaleString('es-MX', { maximumFractionDigits: 2 })} m²
                  </p>
                </div>
              )}
              {property.pricePerM2 != null && (
                <div className="rounded-xl border border-gray-200 bg-white p-4">
                  <p className="text-xs font-medium text-gray-500">Precio / m²</p>
                  <p className="mt-1 text-base font-bold text-gray-900">{fmt(property.pricePerM2)}</p>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      <div>
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Documentos del activo</h2>
          <p className="text-sm text-gray-500 mt-1">
            Archivos compartidos por la administración para esta propiedad.
          </p>
        </div>
        <Card>
          {docsWithFile.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {docsWithFile.map((document) => (
                <DocumentViewer
                  key={document.id}
                  title={document.title}
                  fileUrl={document.fileUrl!}
                  fileTypeLabel={`${(document.fileType?.toUpperCase() || 'DOC')} · ${formatDate(document.uploadedAt)}`}
                  downloadName={
                    document.title
                      ? `${document.title}.${document.fileType || 'pdf'}`
                      : undefined
                  }
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">Aún no hay documentos publicados para esta propiedad.</p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};
